import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Mail, Phone, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const ClaimSeatPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const code = searchParams.get('code');
  
  const [verifying, setVerifying] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });
  
  useEffect(() => {
    if (!code) {
      setVerifying(false);
      setError('No invitation code provided');
      return;
    }
    
    verifyCode();
  }, [code]);
  
  const verifyCode = async () => {
    try {
      const response = await API.get(`/claim-seat/${code}`);
      setInvitation(response.data);
      setForm(prev => ({
        ...prev,
        email: response.data.email || '',
        full_name: response.data.full_name || ''
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setVerifying(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await API.post('/claim-seat', {
        invitation_code: code,
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        phone: form.phone
      });
      
      toast.success('Account created successfully!');
      
      // Auto-login with the returned token
      if (response.data.access_token && response.data.user) {
        login(response.data.access_token, response.data.user);
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
      setSubmitting(false);
    }
  };
  
  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2C5530] mx-auto mb-4" />
          <p className="text-[#57534E]">Verifying invitation...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E7E5E4] p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Invalid Invitation</h1>
          <p className="text-[#57534E] mb-6">{error}</p>
          <p className="text-sm text-[#737373] mb-6">
            Please contact your coach for a new invitation link.
          </p>
          <Link
            to="/"
            className="inline-block bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E7E5E4] p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-12 w-auto"
            />
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
              Quiet Mind Golf
            </span>
          </Link>
          
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">
            Claim Your Pro Subscription
          </h1>
          <p className="text-[#57534E]">
            <span className="font-semibold">{invitation?.coach_name}</span> has invited you to join Quiet Mind Golf with a complimentary Pro subscription.
          </p>
        </div>
        
        {/* Benefits */}
        <div className="bg-[#F2F0E9] p-4 mb-6">
          <p className="text-sm font-medium text-[#1A1A1A] mb-2">Your Pro membership includes:</p>
          <ul className="text-sm text-[#57534E] space-y-1">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#2C5530]" />
              Unlimited session logging
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#2C5530]" />
              Full access to all features
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#2C5530]" />
              Detailed reports & analytics
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#2C5530]" />
              Direct connection to your coach
            </li>
          </ul>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="claim-seat-form">
          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type="email"
                value={form.email}
                disabled
                className="w-full pl-10 py-3 border border-[#E7E5E4] bg-[#F2F0E9] text-[#737373] cursor-not-allowed"
              />
            </div>
          </div>
          
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Full Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full pl-10 py-3 border border-[#E7E5E4] focus:border-[#2C5530] focus:outline-none"
                placeholder="Your full name"
                required
                data-testid="claim-name-input"
              />
            </div>
          </div>
          
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Create Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full pl-10 pr-12 py-3 border border-[#E7E5E4] focus:border-[#2C5530] focus:outline-none"
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                data-testid="claim-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {/* Phone (optional) */}
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Phone (optional)
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full pl-10 py-3 border border-[#E7E5E4] focus:border-[#2C5530] focus:outline-none"
                placeholder="+44 7123 456789"
                data-testid="claim-phone-input"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2C5530] text-white py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="claim-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account & Start'
            )}
          </button>
        </form>
        
        <p className="text-center text-sm text-[#737373] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#2C5530] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ClaimSeatPage;
